import { Music2 } from 'lucide-react'
import { motion } from 'motion/react'
import type { ThemeSong } from '@/hooks/useInstructorThemeStats'
import { PlayButton } from '@/components/player/PlayButton'
import { songTrack } from '@/components/player/tracks'
import { CountUp } from '@/components/dashboard/fx/CountUp'
import { Reveal } from '@/components/dashboard/fx/Reveal'
import { EASE } from '@/components/dashboard/fx/motion'
import { workoutNouns } from '@/lib/discipline'

interface SongLeaderboardProps {
  songs: ThemeSong[]
}

// A theme like "80s" can span an instructor's classes across more than
// one discipline, so "workouts" rather than "rides".
const { plural: WORKOUTS } = workoutNouns('all')

export function SongLeaderboard({ songs }: SongLeaderboardProps) {
  const max = Math.max(...songs.map((s) => s.ride_count), 1)

  return (
    <ol>
      {songs.map((song, index) => (
        <li key={song.id}>
          <Reveal delay={index * 0.05} y={24}>
            <div className="group flex items-center gap-3 border-b border-border/60 py-4 transition-[padding] duration-300 hover:pl-3 sm:gap-4">
              <span
                className={`w-[3.25rem] shrink-0 font-display text-5xl font-semibold leading-none tracking-tighter transition-colors sm:w-16 sm:text-6xl ${
                  index === 0 ? 'text-gradient' : 'text-outline group-hover:text-foreground'
                }`}
              >
                {String(index + 1).padStart(2, '0')}
              </span>
              <div className="relative size-12 shrink-0 overflow-hidden rounded-lg ring-1 ring-border">
                {song.album_image_url ? (
                  <img src={song.album_image_url} alt="" loading="lazy" className="size-full object-cover" />
                ) : (
                  <div className="flex size-full items-center justify-center bg-muted text-muted-foreground">
                    <Music2 className="size-4" />
                  </div>
                )}
                <PlayButton
                  variant="overlay"
                  track={songTrack(song)}
                />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 truncate text-lg font-medium sm:text-xl">{song.title}</span>
                  <span className="shrink-0 text-sm text-muted-foreground">
                    <CountUp value={song.ride_count} className="font-medium text-foreground" /> {WORKOUTS}
                  </span>
                </div>
                <span className="truncate text-sm text-muted-foreground">{song.artists.join(', ')}</span>
                <div className="h-[3px] w-full overflow-hidden rounded-full bg-muted">
                  <motion.div
                    className="h-full origin-left rounded-full bg-gradient-to-r from-primary to-[#e87ba4]"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: song.ride_count / max }}
                    viewport={{ once: true }}
                    transition={{ duration: 1.4, delay: 0.15 + index * 0.05, ease: EASE }}
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </li>
      ))}
    </ol>
  )
}
